import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    KeyboardEvent,
    MouseEvent,
    useMemo,
    ChangeEventHandler,
    useId,
    ReactNode,
    forwardRef,
} from 'react';
import classNames from 'classnames';
import { Label, SelectField } from './forms';
import { fillRef } from '../../utils/fillRef';

export interface CheckBoxProps extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
> {
    label?: string;
    indeterminate?: boolean;
    _onChange?: (checked: boolean) => void;
}
export const CheckBox = (props: CheckBoxProps) => {
    const { indeterminate, onChange, _onChange, ...rest } = props;
    const ref = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (!ref.current) return;
        ref.current.indeterminate = !!indeterminate;
    }, [ref.current, indeterminate]);
    const actualOnChange = useMemo((): ChangeEventHandler<HTMLInputElement> => {
        if (onChange) {
            if (_onChange) {
                return (event) => {
                    const checked = event.target.checked;
                    onChange(event);
                    _onChange(checked);
                };
            }
            return onChange;
        }
        if (_onChange) {
            return (event) => {
                const checked = event.target.checked;
                _onChange(checked);
            };
        }
        return () => {};
    }, [onChange, _onChange]);
    return <BasicCheckbox ref={ref} onChange={actualOnChange} {...rest} />;
};

export interface BasicCheckboxProps extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
> {
    label?: string;
    className?: string;
    indeterminate?: boolean;
}
const BasicCheckbox = forwardRef<HTMLInputElement, BasicCheckboxProps>(
    ({ label, className, disabled, indeterminate, ...rest }, inputRef) => {
        const id = useId();
        const ref = useRef<HTMLInputElement>(null);
        fillRef(inputRef, ref.current);
        return (
            <LabelAfter
                id={id}
                label={label}
                atomic="checkbox"
                className={classNames(
                    'checkbox',
                    {
                        checked: rest.checked && !indeterminate,
                        indeterminate,
                        disabled,
                    },
                    className
                )}
            >
                <input
                    type="checkbox"
                    id={id}
                    ref={ref}
                    disabled={!!disabled}
                    {...rest}
                />
                <div
                    className="checkbox-box"
                    onClick={() => {
                        ref.current?.click();
                        ref.current?.focus();
                    }}
                >
                    {rest.checked && (
                        <div className="checkbox-check">
                            <i className="fa-solid fa-check" />
                        </div>
                    )}
                    {indeterminate && (
                        <div className="checkbox-check">
                            <i className="fa-solid fa-minus" />
                        </div>
                    )}
                </div>
            </LabelAfter>
        );
    }
);

export interface LabelAfterProps {
    id: string;
    atomic: string;
    label?: string;
    className?: string;
    children?: ReactNode;
}
export const LabelAfter = ({
    id,
    label,
    atomic,
    className,
    children,
}: LabelAfterProps) => {
    const item = (
        <div className={classNames('atomic', atomic, className)}>
            {children}
        </div>
    );
    if (!label) {
        return item;
    }
    return (
        <div className={classNames('atomic', `${atomic}-group`)}>
            {item}
            <Label text={label} htmlFor={id} />
        </div>
    );
};

export interface ThreeStateCheckboxProps {
    disabled?: boolean;
    value?: boolean | null;
    onChange?: (setting: boolean | null) => void;
    defaultValue?: boolean | null;
    className?: string;
}
export const ThreeStateCheckbox = ({
    disabled,
    onChange,
    className,
    value,
    ...rest
}: ThreeStateCheckboxProps) => {
    const [state, setState] = useState<boolean | null>(undefined);
    const display = state === undefined ? value : state;
    const actualOnChange = (input: string) => {
        const newState = input === '1' ? true : input === '0' ? false : null;
        setState(newState === value ? undefined : newState);
        onChange?.(newState);
    };
    useEffect(() => {
        if (value === state) {
            setState(undefined);
        }
    }, [value]);
    return (
        <SelectField
            value={display === true ? '1' : display === false ? '0' : ''}
            className={classNames('three-state', className, {
                active: display !== null,
                inactive: display === null,
                dirty: state !== undefined,
            })}
            options={[
                { text: '有効', value: '1' },
                { text: '無効', value: '0' },
                { text: '未設定', value: '' },
            ]}
            _onChange={actualOnChange}
            {...rest}
        />
    );
};

export type SlideSwitchProps<Name extends string | undefined> = {
    className?: string;
    text?: string;
    textPosition?: 'right' | 'left';
    defaultValue?: boolean;
    disabled?: boolean;
    onChange?: (change: { name: Name; value: boolean }) => void;
    _onChange?: (value: boolean) => void;
} & (Name extends string ? { name: Name } : { name?: undefined });

function isKeyboardEvent(e: { type: string }): e is KeyboardEvent {
    return e.type === 'keydown';
}
export const SlideSwitch = <Name extends string | undefined>(
    props: SlideSwitchProps<Name>
) => {
    const {
        className,
        name,
        text,
        textPosition = 'right',
        defaultValue = false,
        disabled = false,
        onChange,
        _onChange,
    } = props;
    let [on, setOn] = useState<boolean>(defaultValue);
    const onClick = useCallback(
        (e: MouseEvent<HTMLDivElement> | KeyboardEvent) => {
            if (disabled) {
                return false;
            }
            if (isKeyboardEvent(e)) {
                if (e.code !== 'Enter' && e.code !== 'Space') return;
            } else if (e.target instanceof HTMLElement) {
                e.target.focus();
                e.target.blur();
            }
            setOn((on = !on));
            onChange?.({ name: name as Name, value: on });
            _onChange?.(on);
            return false;
        },
        [disabled, onChange, _onChange]
    );
    const id = useId();
    return (
        <div
            className={classNames('atomic slide-switch-box-wrapper', {
                disabled,
            })}
        >
            <div
                className={classNames('atomic slide-switch-box', className, {
                    on,
                })}
                onClick={onClick}
                onKeyDown={onClick}
                {...(disabled ? {} : { tabIndex: 0 })}
            >
                <input id={id} name={name} type="hidden" />
                <div className={classNames('atomic slide-switch-pin')} />
            </div>
            {text && (
                <label
                    htmlFor={id}
                    className={classNames('atomic slide-switch-box-label', {
                        left: textPosition === 'left',
                        right: textPosition === 'right',
                    })}
                >
                    {text}
                </label>
            )}
        </div>
    );
};
